import { useEffect, useState, useSyncExternalStore } from "react";
import { DEV_PANEL_ENABLED } from "./transport.js";

/**
 * The dev panel's data, and the hard spend cap.
 *
 * The cap lives here rather than inside the panel component because it has
 * to be able to block a turn even when the panel is collapsed — the whole
 * point is that a runaway loop cannot quietly spend money while the panel is
 * closed. `DEV_PANEL_ENABLED` is a compile-time constant, so in a production
 * build every consumer of this module folds to a constant and the module is
 * tree-shaken out entirely.
 */

export interface DevLlmCall {
  readonly id: string;
  readonly role: string;
  readonly provider: string;
  readonly model: string;
  readonly promptId: string;
  readonly promptVersion: string;
  readonly renderedPrompt: string | null;
  readonly rawResponse: string | null;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheCreationTokens: number;
  readonly costUsd: number;
  readonly latencyMs: number;
  readonly attempt: number;
  readonly validationOutcome: string;
  readonly createdAt: string;
}

export interface DevSpan {
  readonly turn: number;
  readonly step: string;
  readonly ms: number;
}

export interface DevEvent {
  readonly seq: number;
  readonly event: Record<string, unknown>;
}

export interface DevTelemetry {
  readonly sessionId: string;
  readonly seed: string;
  readonly status: string;
  readonly llmMode: string;
  readonly spans: readonly DevSpan[];
  readonly llmCalls: readonly DevLlmCall[];
  readonly events: readonly DevEvent[];
}

export const DEFAULT_SPEND_CAP_USD = 2.0;
const CAP_KEY = "pc.dev.spendCapUsd";

interface BudgetState {
  readonly capUsd: number;
  readonly spentUsd: number;
}

let budget: BudgetState = { capUsd: readCap(), spentUsd: 0 };
const listeners = new Set<() => void>();

function readCap(): number {
  try {
    const raw = globalThis.localStorage?.getItem(CAP_KEY);
    const parsed = raw === null || raw === undefined ? Number.NaN : Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_SPEND_CAP_USD;
  } catch {
    return DEFAULT_SPEND_CAP_USD;
  }
}

function emit(): void {
  for (const l of listeners) l();
}

export function setSpendCap(capUsd: number): void {
  budget = { ...budget, capUsd };
  try {
    globalThis.localStorage?.setItem(CAP_KEY, String(capUsd));
  } catch {
    /* no storage — the cap simply does not survive a reload */
  }
  emit();
}

export function setSpentUsd(spentUsd: number): void {
  if (budget.spentUsd === spentUsd) return;
  budget = { ...budget, spentUsd };
  emit();
}

export function useBudget(): BudgetState & { readonly blocked: boolean } {
  const snapshot = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => budget,
    () => budget,
  );
  return { ...snapshot, blocked: DEV_PANEL_ENABLED && snapshot.spentUsd >= snapshot.capUsd };
}

/**
 * Polls the dev-only telemetry route. Deliberately a plain effect rather
 * than a TanStack Query — it must never retry, never cache across sessions,
 * and never appear in the query devtools alongside real product data.
 *
 * `refreshToken` is bumped by the play surface after every turn so the panel
 * updates exactly when there is something new, instead of on a timer.
 */
export function useDevTelemetry(sessionId: string | null, refreshToken: number): DevTelemetry | null {
  const [telemetry, setTelemetry] = useState<DevTelemetry | null>(null);

  useEffect(() => {
    if (!DEV_PANEL_ENABLED || sessionId === null) return;
    let cancelled = false;
    void (async () => {
      const data = await fetchTelemetry(sessionId);
      if (cancelled || data === null) return;
      setTelemetry(data);
      setSpentUsd(data.llmCalls.reduce((sum, c) => sum + c.costUsd, 0));
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshToken]);

  return telemetry;
}

/**
 * The one deliberate exception to "no fetch outside packages/client", and it
 * is annotated as such for eslint. `/__dev/*` is not part of the product's
 * HTTP contract — it exists only when PC_DEV_PANEL=1, returns hidden ground
 * truth, and must never be given a typed client method that a product
 * component could reach by accident.
 */
async function fetchTelemetry(sessionId: string): Promise<DevTelemetry | null> {
  try {
    // eslint-disable-next-line no-restricted-globals -- dev-only telemetry; see the note above.
    const response = await fetch(`/api/__dev/sessions/${sessionId}/telemetry`);
    if (!response.ok) return null;
    return (await response.json()) as DevTelemetry;
  } catch {
    return null;
  }
}

export async function fetchDevMeta(): Promise<{ llmMode: string; modelsByRole: Record<string, string> } | null> {
  if (!DEV_PANEL_ENABLED) return null;
  try {
    // eslint-disable-next-line no-restricted-globals -- dev-only telemetry; see the note above.
    const response = await fetch("/api/__dev/meta");
    if (!response.ok) return null;
    return (await response.json()) as { llmMode: string; modelsByRole: Record<string, string> };
  } catch {
    return null;
  }
}
